import { ApplicationApiClient } from 'api'
import styles from './EditSubmittedApplicationPage.module.css'
import { routes } from 'routing'
import { Agreement } from 'model'
import { BusyIndicator, PageHeader, ProgressModal } from '@framework'
import { useApiClient, useSession, useTranslation } from '@hooks'
import React, { useContext, useMemo, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Divider, Menu, Tab } from 'semantic-ui-react'
import { CalculatorTab } from '.'
import useAgreements from './dataProviders/useAgreements'
import useChangedParameterCodes from './dataProviders/useChangedParameterCodes'
import useDefaultApplication from './dataProviders/useDefaultApplication'
import useAvailableObjectTypes from './dataProviders/useAvailableObjectTypes'
import { ApplicationContext, ApplicationContextProvider } from './state'
import { ApplicantType, ApplicationType, DPButton } from 'shared'
import useLatestVersionOfApplication from './dataProviders/useLatestVersionOfApplication'

export type ApplicationTab = 'object' | 'calculation' | 'customer' | 'submission'

export function EditSubmittedApplicationPage() {
    const session = useSession()
    const { id: referenceId } = useParams()
    const dealerId = session.currentDealer?.id
    const { changedParameterCodes } = useChangedParameterCodes(dealerId)
    const { agreements, isLoadingAgreements } = useAgreements(dealerId, ApplicationType.Loan, ApplicantType.Private)
    const [latestSavedApplication, isLoadingApplication] = useLatestVersionOfApplication(referenceId, dealerId)
    const [defaultApplication] = useDefaultApplication(dealerId!, ApplicationType.Loan, ApplicantType.Private)

    const isLoading =
        isLoadingAgreements ||
        isLoadingApplication;

    return (
        <article>
            <BusyIndicator busy={isLoading}>
                {defaultApplication !== undefined && latestSavedApplication !== undefined &&
                    <ApplicationContextProvider
                        changedParameterCodes={changedParameterCodes}
                        application={latestSavedApplication.application}
                        defaultApplication={defaultApplication}
                        isEditMode={true}
                    >
                        <EditSubmittedApplicationPageView
                            agreements={agreements}
                            applicationType={ApplicationType.Loan}
                            referenceId={referenceId!}
                            caseNumber={latestSavedApplication.caseNumber}
                        />
                    </ApplicationContextProvider>
                }
            </BusyIndicator>
        </article>
    )
}

interface IEditSubmittedApplicationPageViewProps {
    agreements: Agreement[],
    applicationType: ApplicationType,
    referenceId: string,
    caseNumber: string
}

function EditSubmittedApplicationPageView(props: IEditSubmittedApplicationPageViewProps) {
    const context = useContext(ApplicationContext)
    const { application, isLoading: isLoadingContext, calculationResult } = context
    const $t = useTranslation('ApplicationPage')
    const $summaryTranslation = useTranslation('SummaryTab')
    const session = useSession()
    const applicationApiClient = useApiClient((a) => new ApplicationApiClient(a))
    const dealerId = session.currentDealer?.id
    const objectTypes = useAvailableObjectTypes(props.agreements)
    const [resendingApplication, setResendingApplication] = useState(false);
    const [allowToProceed, setAllowToProceed] = useState(true);
    const [resendRequested, setResendRequested] = useState(false); 

    const headerText = useMemo(() => `${$t('CustomizeApplication')} ${props.caseNumber}`, [props.caseNumber, $t])

    const isLoading = isLoadingContext || resendingApplication

    const navigate = useNavigate()

    const calcSignature = React.useMemo(() => {
        if (!calculationResult) return '';
        const c = calculationResult.summary;
       
        //return a json string as signature for c
        const signature = JSON.stringify(c, null, 2);        
        return signature;

    }, [calculationResult]);

    // Fire resend when requested AND not loading AND calculation finished without errors
    useEffect(() => {

        if (
            resendRequested &&
            !isLoadingContext &&
            !resendingApplication &&
            calculationResult &&
            !calculationResult.errors?.length
        ) {
            // guard to avoid double triggers
            setResendRequested(false);            
            resendApplication();
        }
    }, [calcSignature, resendRequested]); 

    const resendApplication = async () => {
        setResendingApplication(true)
        try {
            const response = await applicationApiClient.resendApplication({
                dealerId: dealerId!,
                brandCode: session.currentDealer?.brandCode!,
                referenceId: props.referenceId,
                application,
                configuration: { retries: 3 },
            })

            if (response.success) {
                navigate(routes.applicationSubmittedApplication(props.referenceId))
            } else {
                setResendingApplication(false)
            }
        } catch {
            setResendingApplication(false)
        }
    }

    const panes = [
        {
            menuItem: <Menu.Item key="Calculation">{$t('Calculation')}</Menu.Item>,
            render: () => (
                <Tab.Pane key="Calculation">
                    <BusyIndicator busy={isLoading}>
                        <CalculatorTab
                            agreements={props.agreements}
                            objectTypes={objectTypes}
                            applicationType={props.applicationType}
                            allowToProceed={allowToProceed}
                            setAllowToProceed={setAllowToProceed}
                        />
                    </BusyIndicator>
                </Tab.Pane>
            )
        }
    ]
    
    return (
        <>
            <PageHeader text={headerText} />
            <Tab
                menu={{ secondary: true, pointing: true }}
                renderActiveOnly={true}
                activeIndex={0}
                panes={panes}
            />
            <ProgressModal
                open={resendingApplication}
                header={$summaryTranslation('Application is being send')}
                message={$summaryTranslation('It can take some time to send the application')}
            />
            <Divider />
            <div className={styles['buttons-container']}>
                <DPButton
                    onClick={() => navigate(routes.applicationSubmittedApplication(props.referenceId))}
                    size="mini"
                    disabled={resendingApplication}>
                    {$t('Cancel')}
                </DPButton>
                <DPButton
                   onClick={() => {                      
                       setResendRequested(true);
                    }}
                    size="mini"
                    disabled={!allowToProceed || resendingApplication || !!calculationResult?.errors?.length}>
                    {$t('Resend')}
                </DPButton>
            </div>
        </>
    )
}